import React from "react";
import type { NeedsAttentionDetails, NeedsAttentionTemplate } from "@core/reports/utils/reportWorkflow";

type Props = {
  details: NeedsAttentionDetails;
};

function itemLabel(activityName: string | null, kpiName: string | null, subGroupName: string | null): string {
  const activity = activityName ?? "Activity";
  const kpi = kpiName ?? "KPI";
  const subGroup = subGroupName ? ` (${subGroupName})` : "";
  return `${activity} → ${kpi}${subGroup}`;
}

function TemplateGapList({ templates }: { templates: NeedsAttentionTemplate[] }) {
  return (
    <ul className="mb-0 ps-3">
      {templates.map((template) => (
        <li key={template.templateId} className="mb-1">
          <div className="fw-medium">{template.templateName}</div>
          {template.items.length === 0 ? (
            <div className="text-muted">Template not submitted</div>
          ) : (
            <ul className="mb-0 ps-3">
              {template.items.map((item, index) => (
                <li key={`${item.activityId ?? "a"}-${item.kpiId ?? "k"}-${index}`}>
                  {itemLabel(item.activityName, item.kpiName, item.subGroupName)}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function NeedsAttentionDetailsList({ details }: Props) {
  const childHeading =
    details.unapprovedChildren.some((child) => child.childScope.toUpperCase() === "DISTRICT")
      ? "Required districts not approved"
      : "Required churches not approved";

  return (
    <div
      className="mt-2"
      style={{ maxHeight: "16rem", overflowY: "auto" }}
    >
      {details.templates.length > 0 ? (
        <div className="mb-2">
          <TemplateGapList templates={details.templates} />
        </div>
      ) : null}

      {details.officeTemplates.length > 0 ? (
        <div className="mb-2">
          <div className="fw-semibold mb-1">Office</div>
          <TemplateGapList templates={details.officeTemplates} />
        </div>
      ) : null}

      {details.unapprovedChildren.length > 0 ? (
        <div className="mb-2">
          <div className="fw-semibold mb-1">{childHeading}</div>
          <ul className="mb-0 ps-3">
            {details.unapprovedChildren.map((child) => (
              <li key={`${child.childScope}-${child.id}`}>{child.name}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {details.churches.length > 0 ? (
        <div className="mb-2">
          <div className="fw-semibold mb-1">Churches with missing items</div>
          {details.churches.map((church) => (
            <div key={church.churchId} className="mb-1">
              <div className="fw-medium">{church.churchName}</div>
              <TemplateGapList templates={church.templates} />
            </div>
          ))}
        </div>
      ) : null}

      {details.districts.length > 0 ? (
        <div className="mb-0">
          <div className="fw-semibold mb-1">Districts</div>
          {details.districts.map((district) => (
            <div key={district.districtId} className="mb-2">
              <div className="fw-medium">{district.districtName}</div>
              {district.churches.map((church) => (
                <div key={church.churchId} className="ms-2 mb-1">
                  <div className="fw-medium">{church.churchName}</div>
                  <TemplateGapList templates={church.templates} />
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
